import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  ArrowLeft,
  AlertCircle,
  Calendar,
  Grid,
  Columns,
  Square,
  Play,
  Film,
  Palette,
  LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAYOUT_ICONS = {
  masonry: Grid,
  justified: Columns,
  grid: Square,
  slideshow: Play,
  filmstrip: Film
};

export default function CreateGalleryPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [galleryOptions, setGalleryOptions] = useState({ layouts: [], themes: {} });
  const [billingAccount, setBillingAccount] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    client_name: '',
    client_email: '',
    password: '',
    expires_at: '',
    layout: 'masonry',
    theme: 'dark-elegant',
    watermark_enabled: true,
    download_enabled: true,
    selection_enabled: true,
    comments_enabled: true
  });

  useEffect(() => {
    fetchGalleryOptions();
    fetchBillingAccount();
  }, []);

  const fetchBillingAccount = async () => {
    try {
      const res = await axios.get(`${API}/billing/account`);
      setBillingAccount(res.data);
    } catch { /* ignore */ }
  };

  const fetchGalleryOptions = async () => {
    try {
      const response = await axios.get(`${API}/gallery-options`);
      setGalleryOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery options');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.client_name) {
      toast.error(language === 'pl' ? 'Wypełnij wymagane pola' : 'Fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/galleries`, {
        ...formData,
        password: formData.password || null,
        expires_at: formData.expires_at || null,
        client_email: formData.client_email || null
      });
      toast.success(t('common.success'));
      navigate(`/galleries/${response.data.id}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(language === 'pl' ? 'Brak kredytow — dokup pakiet' : 'No credits — buy a pack');
        navigate('/billing');
        return;
      }
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const selectedTheme = galleryOptions.themes[formData.theme];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/galleries" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/galleries')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-3xl font-bold">{t('gallery.create')}</h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {billingAccount && !billingAccount.annual_unlimited &&
            (billingAccount.credits_s || 0) + (billingAccount.credits_m || 0) + (billingAccount.credits_l || 0) === 0 && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-500">
                  {language === 'pl' ? 'Brak kredytow' : 'No credits'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'pl'
                    ? 'Dokup pakiet, aby moc tworzyc galerie.'
                    : 'Buy a pack to create galleries.'}
                </p>
              </div>
              <Button size="sm" onClick={() => navigate('/billing')}>
                {language === 'pl' ? 'Dokup' : 'Buy'}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Basic Info */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {language === 'pl' ? 'Podstawowe informacje' : 'Basic Information'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('gallery.name')} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder={language === 'pl' ? 'np. Ślub Ani i Piotra' : 'e.g. Anna & Peter Wedding'}
                      className="h-12"
                      data-testid="gallery-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="client_name">{t('gallery.clientName')} *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => handleChange('client_name', e.target.value)}
                      placeholder={language === 'pl' ? 'np. Anna Kowalska' : 'e.g. Anna Smith'}
                      className="h-12"
                      data-testid="gallery-client-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="client_email">
                      {language === 'pl' ? 'Email klienta' : 'Client Email'}
                    </Label>
                    <Input
                      id="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => handleChange('client_email', e.target.value)}
                      placeholder="email@example.com"
                      className="h-12"
                      data-testid="gallery-client-email-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'pl' ? 'Do wysyłania powiadomień' : 'For sending notifications'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">{t('gallery.password')}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="••••••••"
                      className="h-12"
                      data-testid="gallery-password-input"
                    />
                    <p className="text-xs text-muted-foreground">{t('gallery.passwordHint')}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expires_at">{t('gallery.expiresAt')}</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="expires_at"
                        type="date"
                        value={formData.expires_at}
                        onChange={(e) => handleChange('expires_at', e.target.value)}
                        className="h-12 pl-10"
                        data-testid="gallery-expires-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Layout & Theme */}
              <div className="space-y-8">
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <LayoutGrid className="w-5 h-5" />
                      {language === 'pl' ? 'Układ galerii' : 'Gallery Layout'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-2">
                      {galleryOptions.layouts.map((layout) => {
                        const Icon = LAYOUT_ICONS[layout] || Grid;
                        return (
                          <button
                            key={layout}
                            type="button"
                            onClick={() => handleChange('layout', layout)}
                            className={`p-4 rounded-sm border-2 flex flex-col items-center gap-2 transition-all ${
                              formData.layout === layout 
                                ? 'border-primary bg-primary/10' 
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <Icon className="w-6 h-6" />
                            <span className="text-xs capitalize">{layout}</span>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Palette className="w-5 h-5" />
                      {language === 'pl' ? 'Motyw kolorystyczny' : 'Color Theme'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-2 mb-4">
                      {Object.entries(galleryOptions.themes).map(([key, theme]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleChange('theme', key)}
                          className={`p-3 rounded-sm border-2 flex flex-col items-center gap-1 transition-all ${
                            formData.theme === key 
                              ? 'border-primary' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          title={theme.name}
                        >
                          <div 
                            className="w-8 h-8 rounded-sm border"
                            style={{ 
                              backgroundColor: theme.bg,
                              borderColor: theme.accent
                            }}
                          >
                            <div 
                              className="w-full h-1/2 rounded-t-sm"
                              style={{ backgroundColor: theme.card }}
                            />
                          </div>
                          <span className="text-[10px] truncate w-full text-center">{theme.name}</span>
                        </button>
                      ))}
                    </div>
                    
                    {/* Theme Preview */}
                    {selectedTheme && (
                      <div 
                        className="p-4 rounded-sm border"
                        style={{ 
                          backgroundColor: selectedTheme.bg,
                          borderColor: selectedTheme.accent,
                          color: selectedTheme.text
                        }}
                      >
                        <p className="text-sm font-medium mb-2">{selectedTheme.name}</p>
                        <div className="flex gap-2">
                          <div 
                            className="w-12 h-8 rounded-sm"
                            style={{ backgroundColor: selectedTheme.card }}
                          />
                          <div 
                            className="w-12 h-8 rounded-sm"
                            style={{ backgroundColor: selectedTheme.accent }}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('settings.title')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-border">
                      <div>
                        <p className="font-medium">{t('gallery.watermark')}</p>
                        <p className="text-sm text-muted-foreground">
                          {language === 'pl' ? 'Dodaj znak wodny do zdjęć' : 'Add watermark to photos'}
                        </p>
                      </div>
                      <Switch
                        checked={formData.watermark_enabled}
                        onCheckedChange={(checked) => handleChange('watermark_enabled', checked)}
                        data-testid="gallery-watermark-switch"
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-border">
                      <div>
                        <p className="font-medium">{t('gallery.downloadEnabled')}</p>
                        <p className="text-sm text-muted-foreground">
                          {language === 'pl' ? 'Zezwól klientom na pobieranie' : 'Allow clients to download'}
                        </p>
                      </div>
                      <Switch
                        checked={formData.download_enabled}
                        onCheckedChange={(checked) => handleChange('download_enabled', checked)}
                        data-testid="gallery-download-switch"
                      />
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-border">
                      <div>
                        <p className="font-medium">
                          {language === 'pl' ? 'Wybór ulubionych' : 'Favorites Selection'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {language === 'pl' ? 'Pozwól klientom oznaczać ulubione' : 'Allow clients to mark favorites'}
                        </p>
                      </div>
                      <Switch
                        checked={formData.selection_enabled}
                        onCheckedChange={(checked) => handleChange('selection_enabled', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-medium">
                          {language === 'pl' ? 'Komentarze' : 'Comments'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {language === 'pl' ? 'Pozwól klientom komentować' : 'Allow clients to comment'}
                        </p>
                      </div>
                      <Switch
                        checked={formData.comments_enabled}
                        onCheckedChange={(checked) => handleChange('comments_enabled', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-end gap-4 mt-8">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate('/galleries')}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                data-testid="create-gallery-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {t('gallery.create')}
              </Button>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
